import { DateRangePicker } from '@modules/common/components/DateRangePicker';
import { DEFAULT_DATE_TIME_RANGE } from '@modules/common/components/DateRangePicker/constants';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { WhereClausePreview } from '@modules/common/components/WhereClausePreview';
import { OracleConversationMarkdown } from '@modules/oracle/components/OracleConversationMarkdown';
import { OracleResponseStepSubmitButton } from '@modules/oracle/components/OracleSearchResponseStepDetailsDialog/OracleResponseStepSubmitButton';
import { WisdomSearchResponseStepDetailsDialogBanner } from '@modules/oracle/components/OracleSearchResponseStepDetailsDialog/WisdomSearchResponseStepDetailsDialogBanner';
import {
  constructMarkdownFromAssistantResponseToken,
  getDurationFromAssistantResponseEqlQuery,
  getWhereClauseFromAssistantResponseEqlQuery,
  trackWisdomEvents,
} from '@modules/oracle/utils';
import { getMarkdownForChatResponse } from '@modules/oracle/utils/getMarkdownForChatResponse';
import { QueryBuilder } from '@modules/queryBuilder/components/QueryBuilder';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import CalendarTodayRounded from '@mui/icons-material/CalendarTodayRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { AssistantResponseTokenV2, EqlQuery } from 'generated/graphql';
import { useEffect, useMemo, useState } from 'react';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';

interface OracleSummaryStepDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  assistantResponseTokenV2List: AssistantResponseTokenV2[] | undefined;
  eqlQuery: EqlQuery;
  isReadOnly?: boolean;
  shouldSkipTypingAnimation?: boolean;
  promptID: string;
  isLoading?: boolean;
  stepID: string;
  stepTitle: string;
}

export function OracleSummaryStepDetailsDialog({
  onClose,
  isOpen,
  assistantResponseTokenV2List,
  eqlQuery,
  isReadOnly,
  promptID,
  isLoading,
  stepID,
  stepTitle,
  shouldSkipTypingAnimation = false,
}: OracleSummaryStepDetailsDialogProps) {
  const {
    tenant: { timezone },
  } = useGetUser();
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const [duration, setDuration] = useState<DateTimeRange>(
    DEFAULT_DATE_TIME_RANGE,
  );
  const [whereClause, setWhereClause] = useState<WhereClause>();

  const markdown = useMemo(
    () =>
      constructMarkdownFromAssistantResponseToken({
        assistantResponseTokenV2List,
        tokenToMarkdownConverter: getMarkdownForChatResponse,
      }),
    [assistantResponseTokenV2List],
  );

  const isInvalidQuery = useQueryBuilderStore(
    ({ metadata }) => !!metadata.error || metadata.hasIncompleteFilters,
  );

  const defaultWhereClause = useMemo(
    () =>
      getWhereClauseFromAssistantResponseEqlQuery({
        sourceDetailsMap,
        timezone,
        whereClause: eqlQuery.properties.whereClause,
      }),
    [eqlQuery.properties.whereClause, sourceDetailsMap, timezone],
  );

  const defaultDuration = useMemo(
    () =>
      getDurationFromAssistantResponseEqlQuery(eqlQuery.properties.duration),
    [eqlQuery],
  );

  useEffect(() => {
    setDuration(defaultDuration);
  }, [defaultDuration]);

  const [isDurationModified, setIsDurationModified] = useState(false);
  const isWhereClauseModified = useQueryBuilderStore(({ metadata }) => {
    return (
      metadata.hasUnsavedChanges &&
      !(metadata.hasIncompleteFilters || Boolean(metadata.error))
    );
  });

  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const eqlProps: Omit<EQLProps, 'sourceDetailsMap'> = {
    expressionType: ExpressionType.Records,
    whereClause,
    dateRange: duration as EQLProps['dateRange'],
  };
  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  const areInputsModified = isDurationModified || isWhereClauseModified;

  const onCopy = () => {
    const selectedText = window.getSelection()?.toString();

    trackWisdomEvents({
      eventName: 'Wisdom - Manually Copy Response',
      response: selectedText,
      copiedFrom: 'details dialog',
    });
  };

  const renderQueryInputs = () => {
    return (
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarTodayRounded
            sx={{
              color: 'text.secondary',
            }}
          />
          <DateRangePicker
            timeRange={duration}
            shouldShowQuickLookupOptions
            onChange={(duration) => {
              setIsDurationModified(true);
              setDuration(duration);
            }}
            quickOptionsFilters={{ skipYesterday: true }}
            isReadOnly={isReadOnly}
          />
        </Stack>

        {isReadOnly ? (
          <WhereClausePreview whereClause={defaultWhereClause} />
        ) : (
          <QueryBuilder
            whereClause={defaultWhereClause}
            onChange={({ whereClause }) => {
              setWhereClause(whereClause);
            }}
            actionsProps={{
              shouldRenderExecuteButton: false,
            }}
            footerProps={{
              shouldRenderCreateNewFilter: false,
            }}
            shouldSkipWarningMessage={true}
          />
        )}

        <Box>
          <Divider
            sx={{
              ml: -2,
              mr: -2,
              opacity: 0.1,
            }}
          />
        </Box>
      </Stack>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ elevation: 0 }}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer,
        '& .MuiDialogContent-root': {
          padding: (theme) => theme.spacing(2),
        },
        '& .MuiDialogActions-root': {
          padding: (theme) => theme.spacing(1),
        },
      }}
    >
      <ModalHeader
        title={`${isReadOnly ? 'Inspect' : 'Edit'} Query`}
        onClose={onClose}
      />
      <Divider
        sx={{
          opacity: 0.12,
        }}
      />
      <WisdomSearchResponseStepDetailsDialogBanner stepTitle={stepTitle} />
      <DialogContent dividers>
        <Stack>
          {renderQueryInputs()}
          <Box sx={{ minHeight: 390, position: 'relative' }}>
            <Box sx={{ height: '100%', mt: 2 }}>
              {!!markdown ? (
                <Box onCopy={onCopy}>
                  <OracleConversationMarkdown
                    markdown={markdown}
                    shouldSkipTypingAnimation={true}
                    isLoading={isLoading}
                    promptID={promptID}
                    stepID={stepID}
                  />
                </Box>
              ) : (
                <Stack spacing={1}>
                  <Skeleton />
                  <Skeleton />
                </Stack>
              )}
            </Box>
            {areInputsModified ? (
              <Typography
                variant="subtitle2"
                component={Stack}
                direction="column"
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0.9,
                  backgroundColor: (theme) => theme.palette.background.paper,
                }}
              >
                <Typography component="span" variant="inherit">
                  We noticed you changed the query above.
                </Typography>
                <Typography component="span" variant="inherit">
                  Enterpret will create a new summary that fits the updated
                  query.
                </Typography>
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        {isReadOnly ? null : (
          <OracleResponseStepSubmitButton
            promptID={promptID}
            stepID={stepID}
            eql={eql}
            disabled={isInvalidQuery || !areInputsModified}
            areInputsModified={areInputsModified}
          />
        )}
      </DialogActions>
    </Dialog>
  );
}
